import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { CalendarDays, Plus, Phone, Users, MapPin } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Combobox } from '@/components/ui/combobox';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { Reservation, RestaurantTable } from '@/types/restaurant';
import { Dropdown } from 'primereact/dropdown';

const STATUS_COLORS: Record<string, string> = {
  PENDING:   'bg-warning/10 text-warning-text',
  CONFIRMED: 'bg-info/10 text-info-text',
  SEATED:    'bg-success/10 text-success-text',
  COMPLETED: 'bg-muted text-muted-foreground',
  CANCELLED: 'bg-destructive/10 text-destructive-text',
  NO_SHOW:   'bg-warning/10 text-warning-text',
};

/** `datetime-local` value from local calendar/clock parts (not UTC). */
function localDateTimeInputValue(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}

/** `type="date"` filter default from local calendar date (not UTC). */
function localCalendarDateString(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse `datetime-local` string as local time → absolute ISO instant for the API. */
function datetimeLocalToIso(localValue: string): string {
  const [datePart, timePart] = localValue.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes).toISOString();
}

interface CreateReservationPayload {
  customerName: string;
  customerPhone: string;
  reservationDate: string;
  partySize: number;
  durationMinutes: number;
  tableId?: string;
  notes: string;
  locationId?: string;
}

function createEmptyReservationForm() {
  return {
    customerName: '',
    customerPhone: '',
    reservationDate: localDateTimeInputValue(),
    partySize: 2,
    durationMinutes: 90,
    tableId: '',
    notes: '',
    locationId: '',
  };
}

export function ReservationsPage() {
  const [selectedDate, setSelectedDate] = useState(() => localCalendarDateString());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(createEmptyReservationForm);
  const [cancelReservation, setCancelReservation] = useState<Reservation | null>(null);

  const queryClient = useQueryClient();
  const handleReservationUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['reservations'] });
    queryClient.invalidateQueries({ queryKey: ['restaurant-tables-all'] });
  }, [queryClient]);
  useSocketEvent('dining_order.created', handleReservationUpdate);
  useSocketEvent('dining_order.updated', handleReservationUpdate);
  useSocketEvent('dining_order.billed', handleReservationUpdate);
  useSocketEvent('reservation.updated', handleReservationUpdate);
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const canWrite = hasPermission('reservations:write') || hasPermission('reservations:*') || hasPermission('restaurant:*');

  const openNewReservationDialog = () => {
    setForm(createEmptyReservationForm());
    setDialogOpen(true);
  };

  const { data: reservations, isLoading } = useQuery<Reservation[]>({
    queryKey: ['reservations', selectedDate],
    queryFn: () => api.get(`/reservations?date=${selectedDate}`).then((r) => r.data?.data ?? r.data ?? []),
  });

  const { data: tablesData } = useQuery<RestaurantTable[]>({
    queryKey: ['restaurant-tables-all'],
    queryFn: () => api.get('/restaurant/tables').then((r) => r.data?.data ?? r.data ?? []),
    enabled: dialogOpen,
  });

  const { data: locations } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then((r) => r.data?.data ?? r.data ?? []),
    enabled: dialogOpen,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateReservationPayload) => api.post('/reservations', data),
    onSuccess: () => {
      toast.success(t('reservationCreated'));
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      setDialogOpen(false);
      setForm(createEmptyReservationForm());
    },
    onError: () => toast.error(t('failedToCreateReservation')),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/reservations/${id}/status`, { status }),
    onSuccess: () => {
      toast.success(t('statusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      setCancelReservation(null);
    },
    onError: () => toast.error(t('failedToUpdateStatus')),
  });

  const handleCreate = () => {
    if (!form.customerName || !form.reservationDate) {
      toast.error(t('customerNameAndDateRequired'));
      return;
    }
    if (form.partySize < 1) {
      toast.error(t('partySizeMin'));
      return;
    }
    createMutation.mutate({
      ...form,
      reservationDate: datetimeLocalToIso(form.reservationDate),
      partySize: Number(form.partySize),
      durationMinutes: Number(form.durationMinutes),
      tableId: form.tableId || undefined,
      locationId: form.locationId || undefined,
    });
  };

  const sorted = [...(reservations ?? [])].sort(
    (a, b) => new Date(a.reservationDate).getTime() - new Date(b.reservationDate).getTime()
  );

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader
        title={t('reservations')}
        subtitle={t('reservationsSubtitle')}
        action={
          canWrite ? (
            <Button onClick={openNewReservationDialog}>
              <Plus className="me-2 h-4 w-4" />
              {t('newReservation')}
            </Button>
          ) : undefined
        }
      />

      {/* Date picker */}
      <Card>
        <CardContent className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4">
          <CalendarDays className="h-5 w-5 text-muted-foreground hidden sm:block" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full sm:w-44"
          />
          <span className="text-sm text-muted-foreground">
            {t('reservationCount', { count: sorted.length })}
          </span>
        </CardContent>
      </Card>

      {/* Reservations list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('reservationsDate', { date: new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title={t('noReservations')}
              description={t('noReservationsDate')}
              action={canWrite ? { label: t('newReservation'), onClick: openNewReservationDialog } : undefined}
            />
          ) : (
            <div className="divide-y">
              {sorted.map((res) => (
                <div key={res.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3">
                  {/* Time */}
                  <div className="flex items-center sm:block gap-2 sm:w-20 flex-shrink-0">
                    <p className="text-sm font-bold">
                      {new Date(res.reservationDate).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-muted-foreground">{res.durationMinutes}m</p>
                  </div>
                  {/* Guest info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{res.customerName}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      {res.customerPhone && (
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{res.customerPhone}</span>
                      )}
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{res.partySize} {t('guestsLabel')}</span>
                      {res.table && <span>{t('tableNo', { no: res.table.tableNo })}</span>}
                      {res.location && (
                        <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{res.location.name}</span>
                      )}
                    </div>
                    {res.notes && <p className="text-xs text-muted-foreground mt-0.5 italic">{res.notes}</p>}
                  </div>
                  {/* Status badge + actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium flex-shrink-0 ${STATUS_COLORS[res.status] ?? ''}`}>
                      {res.status}
                    </span>
                    {canWrite && (
                      <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
                        {res.status === 'PENDING' && (
                          <Button size="sm" variant="outline" className="h-9" onClick={() => updateStatusMutation.mutate({ id: res.id, status: 'CONFIRMED' })}>
                            {t('confirm')}
                          </Button>
                        )}
                        {res.status === 'CONFIRMED' && (
                          <Button size="sm" variant="outline" className="h-9" onClick={() => updateStatusMutation.mutate({ id: res.id, status: 'SEATED' })}>
                            {t('seat')}
                          </Button>
                        )}
                        {res.status === 'SEATED' && (
                          <Button size="sm" variant="outline" className="h-9" onClick={() => updateStatusMutation.mutate({ id: res.id, status: 'COMPLETED' })}>
                            {t('complete')}
                          </Button>
                        )}
                        {!['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(res.status) && (
                          <>
                            <Button size="sm" variant="outline" className="text-warning-text h-9" onClick={() => updateStatusMutation.mutate({ id: res.id, status: 'NO_SHOW' })}>
                              {t('noShow')}
                            </Button>
                            <Button size="sm" variant="outline" className="text-destructive-text h-9" onClick={() => setCancelReservation(res)}>
                              {t('cancel')}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Reservation dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => { setDialogOpen(false); setForm(createEmptyReservationForm()); }}
        header={t('newReservation')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('customerName')} <span className="text-destructive-text">*</span></Label>
              <Input
                placeholder={t('fullNamePlaceholder')}
                value={form.customerName}
                onChange={(e) => setForm({ ...form, customerName: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('phoneLabel')}</Label>
              <Input
                placeholder="+92 3xx xxxxxxx"
                value={form.customerPhone}
                onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('dateTimeLabel')} <span className="text-destructive-text">*</span></Label>
              <Input
                type="datetime-local"
                value={form.reservationDate}
                onChange={(e) => setForm({ ...form, reservationDate: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('durationMinutes')}</Label>
              <Input
                type="number"
                min="15"
                step="15"
                value={form.durationMinutes}
                onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('partySize')} <span className="text-destructive-text">*</span></Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={form.partySize}
                onChange={(e) => setForm({ ...form, partySize: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('tableOptional')}</Label>
              <Combobox
                options={(tablesData ?? []).map((tbl) => ({ value: tbl.id, label: `${t('tableNo', { no: tbl.tableNo })} (${tbl.capacity} ${t('seatsLabel')})` }))}
                value={form.tableId}
                onChange={(val) => setForm({ ...form, tableId: val ?? '' })}
                placeholder={t('selectTablePlaceholder')}
                emptyText={t('noTablesFound')}
              />
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block">{t('locationOptional')}</Label>
            <Dropdown
              className="w-full"
              value={form.locationId}
              onChange={(e) => setForm({ ...form, locationId: e.value })}
              options={[
                { label: t('allLocations'), value: '' },
                ...(locations ?? []).map((l) => ({ label: l.name, value: l.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('allLocations')}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">{t('notesLabel')}</Label>
            <Input
              placeholder={t('specialRequestsPlaceholder')}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); setForm(createEmptyReservationForm()); }}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? t('saving') : t('createReservation')}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!cancelReservation}
        title={t('cancelReservation')}
        message={`${t('cancelReservationFor')} ${cancelReservation?.customerName}?`}
        confirmLabel={t('cancelReservation')}
        cancelLabel={t('keepReservation')}
        variant="danger"
        loading={updateStatusMutation.isPending}
        onConfirm={() => cancelReservation && updateStatusMutation.mutate({ id: cancelReservation.id, status: 'CANCELLED' })}
        onCancel={() => setCancelReservation(null)}
      />
    </div>
  );
}
